import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, ChevronRight, ChevronLeft, CheckCircle2, Rocket } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const TOUR_STEPS = [
  {
    targetSelector: '[data-testid="nav-inbox"]',
    title: "Omnichannel Conversations",
    description: "Manage real-time SMS, WhatsApp, and email threads in one central team inbox with AI auto-replies.",
    placement: "right",
    stepBadge: "Step 1 of 4",
  },
  {
    targetSelector: '[data-testid="nav-sites"]',
    title: "Sites & Lead Funnels",
    description: "Create high-converting funnels, agency websites, client portals, and web chat widgets with 1-click AI generation.",
    placement: "right",
    stepBadge: "Step 2 of 4",
  },
  {
    targetSelector: '[data-testid="topbar-ai-btn"]',
    title: "AI Studio & Autonomous Agents",
    description: "Access your Gemini-powered AI Studio for instant copy generation, scenario automation, and autonomous growth agents.",
    placement: "bottom",
    stepBadge: "Step 3 of 4",
  },
  {
    targetSelector: '[data-testid="nav-settings"]',
    title: "Location & Account Setup",
    description: "Configure your business profile, connect Stripe & Google Drive, add team members, and manage custom domains.",
    placement: "right",
    stepBadge: "Step 4 of 4",
  },
];

// Card dimensions - used for clamping
const CARD_W = 340;
const CARD_H = 210;
const PADDING = 16;

function computeCardStyle(targetRect, placement) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;

  if (!targetRect) {
    // If element is not in DOM or hidden, perfectly center the modal
    return {
      top: Math.max(PADDING, Math.round((vh - CARD_H) / 2)),
      left: Math.max(PADDING, Math.round((vw - CARD_W) / 2)),
    };
  }

  let top, left;

  if (placement === "right") {
    top = targetRect.top - 20;
    left = targetRect.right + 18;
    // If not enough room on the right, flip to left
    if (left + CARD_W > vw - PADDING) {
      left = targetRect.left - CARD_W - 18;
    }
  } else if (placement === "bottom") {
    top = targetRect.bottom + 18;
    left = targetRect.left - 140;
  } else {
    top = targetRect.bottom + 18;
    left = targetRect.left;
  }

  // Strict clamp to viewport bounds with margin
  top = Math.max(PADDING, Math.min(top, vh - CARD_H - PADDING));
  left = Math.max(PADDING, Math.min(left, vw - CARD_W - PADDING));

  return { top, left };
}

function computeArrowClasses(targetRect, placement) {
  if (!targetRect) return "";
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;

  if (placement === "right") {
    const fitsRight = targetRect.right + 18 + CARD_W < vw - PADDING;
    if (fitsRight) {
      return "-left-2 top-8 border-r-white dark:border-r-slate-900 border-y-transparent border-l-transparent";
    } else {
      return "-right-2 top-8 border-l-white dark:border-l-slate-900 border-y-transparent border-r-transparent";
    }
  } else if (placement === "bottom") {
    return "-top-2 right-12 border-b-white dark:border-b-slate-900 border-x-transparent border-t-transparent";
  }
  return "";
}

export default function AppGuideTour({ isOpen, onClose }) {
  const { user } = useAuth();
  const tourKey = user?.id ? `launchdeck_app_tour_done_${user.id}` : "launchdeck_app_tour_done";

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 640 : false));

  const step = TOUR_STEPS[currentStepIndex];

  useEffect(() => {
    if (!isOpen) return;

    const updateRect = () => {
      const mobile = window.innerWidth < 640;
      setIsMobile(mobile);

      const el = document.querySelector(step.targetSelector);
      if (el) {
        const r = el.getBoundingClientRect();
        // Ensure element is visible and within screen
        if (r.width > 0 && r.height > 0 && r.top < window.innerHeight && r.bottom > 0) {
          setTargetRect({
            top: r.top,
            left: r.left,
            width: r.width,
            height: r.height,
            bottom: r.bottom,
            right: r.right,
          });
          if (!mobile) {
            el.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
          return;
        }
      }
      // Target element is hidden or not on screen -> center the guide card
      setTargetRect(null);
    };

    updateRect();
    window.addEventListener("resize", updateRect);
    return () => window.removeEventListener("resize", updateRect);
  }, [isOpen, currentStepIndex, step.targetSelector]);

  if (!isOpen) return null;

  const handleNext = () => {
    if (currentStepIndex < TOUR_STEPS.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(tourKey, "true");
    localStorage.removeItem("launchdeck_is_new_user");
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem(tourKey, "true");
    localStorage.removeItem("launchdeck_is_new_user");
    onClose();
  };

  const cardStyle = isMobile ? {} : computeCardStyle(targetRect, step.placement);
  const arrowClasses = isMobile ? "" : computeArrowClasses(targetRect, step.placement);

  // Card content shared between mobile bottom sheet and desktop floating card
  const cardContent = (
    <>
      {/* Card Header: Step Pill, Skip Button */}
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 text-[11px] font-bold">
          <Rocket className="w-3 h-3" />
          <span>{step.stepBadge}</span>
        </div>
        <button
          onClick={handleSkip}
          className="text-xs text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 font-medium transition-colors flex items-center gap-1"
        >
          Skip Guide <X className="w-3 h-3" />
        </button>
      </div>

      {/* Title & Description */}
      <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1.5">
        {step.title}
      </h3>
      <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
        {step.description}
      </p>

      {/* Step dots + navigation */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-1.5">
          {TOUR_STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === currentStepIndex
                  ? "w-5 bg-blue-600"
                  : "w-1.5 bg-slate-200 dark:bg-slate-700"
              }`}
            />
          ))}
        </div>

        <div className="flex items-center gap-2">
          {currentStepIndex > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrev}
              className="h-7 px-2.5 text-xs text-slate-600 dark:text-slate-300"
            >
              <ChevronLeft className="w-3.5 h-3.5 mr-0.5" /> Back
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleNext}
            className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1"
          >
            {currentStepIndex === TOUR_STEPS.length - 1 ? (
              <><CheckCircle2 className="w-3.5 h-3.5" /> Finish</>
            ) : (
              <>Next <ChevronRight className="w-3.5 h-3.5 ml-0.5" /></>
            )}
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-auto">
      {/* Dark overlay backdrop */}
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-xs transition-opacity duration-300" />

      {/* Target Element Highlight Box with pulsing halo - desktop only */}
      {targetRect && !isMobile && (
        <div
          className="absolute rounded-lg border-2 border-blue-500 shadow-[0_0_24px_rgba(37,99,235,0.6)] transition-all duration-300 pointer-events-none"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
          }}
        >
          <div className="absolute -inset-1 rounded-lg border border-blue-400 animate-ping opacity-60" />
        </div>
      )}

      {isMobile ? (
        /* ── MOBILE: Bottom Sheet (always inside viewport) ── */
        <div className="absolute bottom-0 inset-x-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 rounded-t-2xl shadow-2xl p-5 pb-8 pointer-events-auto">
          {/* Drag handle */}
          <div className="w-10 h-1 rounded-full bg-slate-200 dark:bg-slate-700 mx-auto mb-4" />
          {cardContent}
        </div>
      ) : (
        /* ── DESKTOP: Floating Card clamped to viewport ── */
        <div
          className="absolute bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl p-5 text-slate-800 dark:text-slate-100 transition-all duration-300 z-50 pointer-events-auto"
          style={{
            ...cardStyle,
            width: CARD_W,
            maxWidth: `calc(100vw - ${PADDING * 2}px)`,
          }}
        >
          {/* Pointer Arrow */}
          {arrowClasses && (
            <div className={`absolute w-0 h-0 border-8 ${arrowClasses}`} />
          )}
          {cardContent}
        </div>
      )}
    </div>
  );
}
